import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Lock, LogOut, Search, RefreshCw, Trash2, Pencil, ShieldCheck, ShieldOff,
  Users, ShoppingBag, EyeOff, Save, X, ExternalLink, Wallet, BadgeCheck,
  Upload, Link2,
} from "lucide-react";
import {
  adminLogin, adminLogout, adminMe, adminStats,
  adminListOrders, adminListCreators, adminPatchCreator, adminDeleteCreator,
  adminVerifyCreator,
  adminRetryPayout,
  adminListPayouts, adminResyncPayout,
  uploadAvatar, uploadAdminPortfolio,
  formatApiError,
} from "../lib/api";
import StatusPill from "../components/StatusPill";
import { toast } from "sonner";

const STATUS_FILTERS = [
  { key: "", label: "All" },
  { key: "paid", label: "Paid" },
  { key: "in_progress", label: "In progress" },
  { key: "delivered", label: "Delivered" },
  { key: "completed", label: "Completed" },
  { key: "revision_requested", label: "Revision" },
  { key: "cancelled", label: "Cancelled" },
];

export default function Admin() {
  const [authed, setAuthed] = useState(undefined);

  useEffect(() => {
    adminMe().then((r) => setAuthed(!!r.ok)).catch(() => setAuthed(false));
  }, []);

  if (authed === undefined) {
    return <div data-testid="admin-loading" className="max-w-3xl mx-auto px-6 py-20 text-[#606070]">Loading…</div>;
  }
  return authed
    ? <AdminDashboard onLogout={() => setAuthed(false)} />
    : <AdminGate onSuccess={() => setAuthed(true)} />;
}

// ─── Login Gate ────────────────────────────────────────────────────────
function AdminGate({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin(pw);
      onSuccess();
    } catch (err) {
      setError(formatApiError(err, "Wrong password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-gate" className="min-h-[80vh] flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto rounded-full bg-[#00ff88]/10 border border-[#00ff88]/30 flex items-center justify-center mb-3">
            <Lock className="text-[#00ff88]" size={22} />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-white">Admin panel</h1>
          <p className="text-xs text-[#a0a0b0] mt-1">Restricted to platform operators.</p>
        </div>
        <form onSubmit={submit} className="cc-surface rounded-2xl p-6 space-y-4 border border-[#15152a]">
          <div>
            <div className="cc-overline mb-1.5">Admin password</div>
            <input
              data-testid="admin-password"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="w-full bg-[#0d0d1a] border border-[#15152a] rounded-lg px-3 py-2.5 text-sm text-white cc-focus-ring"
              required
              autoFocus
            />
          </div>
          {error && <div data-testid="admin-error" className="text-xs text-[#ff8888] bg-[#ff4444]/5 border border-[#ff4444]/30 rounded-md px-3 py-2">{error}</div>}
          <button
            data-testid="admin-submit"
            disabled={loading}
            className="cc-btn-primary w-full rounded-lg px-4 py-2.5 text-sm disabled:opacity-50"
          >
            {loading ? "Checking…" : "Unlock"}
          </button>
        </form>
        <div className="text-center mt-4">
          <Link to="/" className="text-xs text-[#a0a0b0] hover:text-white">← Back to site</Link>
        </div>
      </div>
    </div>
  );
}

// ─── Dashboard ─────────────────────────────────────────────────────────
function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("orders");
  const [stats, setStats] = useState(null);

  const loadStats = () => adminStats().then(setStats).catch(() => setStats(null));
  useEffect(() => { loadStats(); }, []);

  const handleLogout = async () => {
    try { await adminLogout(); } catch (_) {}
    onLogout();
  };

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
        <div>
          <div className="cc-overline inline-flex items-center gap-2"><ShieldCheck size={11} /> Operator</div>
          <h1 className="text-3xl font-semibold tracking-tight text-white mt-2">Admin panel</h1>
          <p className="text-sm text-[#a0a0b0] mt-1">Manage every order and creator listing on the marketplace.</p>
        </div>
        <button
          onClick={handleLogout}
          data-testid="admin-logout"
          className="px-4 py-2 text-xs border border-[#15152a] bg-[#0a0a14] text-[#ffcccc] rounded-lg hover:border-[#ff4444]/30 inline-flex items-center gap-2"
        >
          <LogOut size={12} /> Logout
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Stat label="Users" value={stats?.users ?? "—"} />
        <Stat label="Creators" value={stats?.creators ?? "—"} accent />
        <Stat label="Companies" value={stats?.companies ?? "—"} />
        <Stat label="Orders" value={stats?.orders ?? "—"} />
      </div>

      <div className="flex gap-1 mb-6 p-1 rounded-lg bg-[#06060c] border border-[#15152a] w-fit">
        <TabBtn id="orders" tab={tab} setTab={setTab} icon={<ShoppingBag size={13} />}>Orders</TabBtn>
        <TabBtn id="creators" tab={tab} setTab={setTab} icon={<Users size={13} />}>Creators</TabBtn>
        <TabBtn id="payouts" tab={tab} setTab={setTab} icon={<Wallet size={13} />}>Payouts</TabBtn>
      </div>

      {tab === "orders" && <OrdersAdmin />}
      {tab === "creators" && <CreatorsAdmin />}
      {tab === "payouts" && <PayoutsAdmin />}
    </div>
  );
}

const TabBtn = ({ id, tab, setTab, children, icon }) => (
  <button
    data-testid={`admin-tab-${id}`}
    onClick={() => setTab(id)}
    className={`px-4 py-1.5 text-xs rounded-md inline-flex items-center gap-1.5 transition ${
      tab === id ? "bg-[#00ff88]/10 text-[#00ff88]" : "text-[#a0a0b0] hover:text-white"
    }`}
  >
    {icon} {children}
  </button>
);

const Stat = ({ label, value, accent }) => (
  <div className="cc-surface rounded-xl p-4">
    <div className="cc-overline">{label}</div>
    <div className={`font-mono text-2xl mt-1 ${accent ? "text-[#00ff88]" : "text-white"}`}>{value}</div>
  </div>
);

// ─── Orders Tab ────────────────────────────────────────────────────────
function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    adminListOrders({ status: status || undefined, q: q || undefined })
      .then(setOrders).catch(() => setOrders([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalVolume = orders.reduce((s, o) => s + (o.price || 0), 0);

  return (
    <div data-testid="admin-orders">
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#606070]" />
          <input
            data-testid="admin-orders-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by order ID, company, creator, game…"
            className="w-full bg-[#0d0d1a] border border-[#15152a] rounded-lg pl-9 pr-3 py-2 text-sm text-white cc-focus-ring"
          />
        </div>
        <button onClick={load} data-testid="admin-orders-refresh" className="px-3 py-2 text-xs border border-[#15152a] bg-[#0a0a14] text-[#a0a0b0] rounded-lg hover:text-white inline-flex items-center gap-1">
          <RefreshCw size={12} /> Refresh
        </button>
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatus(f.key)}
              data-testid={`admin-filter-${f.key || "all"}`}
              className={`px-2.5 py-1.5 text-[11px] rounded-md border transition ${
                status === f.key
                  ? "border-[#00ff88]/40 bg-[#00ff88]/10 text-[#00ff88]"
                  : "border-[#15152a] bg-[#0a0a14] text-[#a0a0b0] hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="ml-auto text-[11px] text-[#606070]">
          {orders.length} orders · ${totalVolume} volume
        </div>
      </div>

      {loading && <div className="text-[#606070] text-sm py-6">Loading orders…</div>}
      {!loading && orders.length === 0 && <div className="text-[#606070] text-sm py-6 italic">No orders match.</div>}

      <div className="overflow-x-auto cc-surface rounded-xl">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-[#606070] border-b border-[#15152a]">
              <Th>ID</Th><Th>Company → Creator</Th><Th>Service</Th><Th>Price</Th><Th>Pay</Th><Th>Status</Th><Th>Created</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} data-testid={`admin-order-${o.id}`} className="border-b border-[#15152a] last:border-0 hover:bg-[#0a0a14]">
                <Td><span className="font-mono text-[#00ff88]">{o.id}</span></Td>
                <Td>
                  <div className="text-white">{o.company_name}</div>
                  <div className="text-[11px] text-[#606070]">→ {o.creator_name}</div>
                </Td>
                <Td><span className="text-xs">{o.content_type} · {o.game}</span></Td>
                <Td><span className="font-mono text-[#00ff88]">${o.price}</span></Td>
                <Td>
                  {o.payment_method === "ltc" ? (
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#00ff88]">⚡ LTC</span>
                  ) : (
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#a0a0b0]">💳 Card</span>
                  )}
                  {o.ltc?.tx_hash && (
                    <a href={`https://blockchair.com/litecoin/transaction/${o.ltc.tx_hash}`} target="_blank" rel="noreferrer" className="block text-[10px] text-[#00ff88] hover:underline font-mono mt-0.5">
                      tx · {o.ltc.tx_hash.slice(0, 10)}…
                    </a>
                  )}
                  {o.ltc_release?.status === "manual_required" && (
                    <button
                      data-testid={`admin-retry-payout-${o.id}`}
                      onClick={async () => {
                        try {
                          const updated = await adminRetryPayout(o.id);
                          toast.success(`Payout ${updated.ltc_release?.status || "queued"}`);
                          load();
                        } catch (e) {
                          toast.error(formatApiError(e, "Retry failed"));
                        }
                      }}
                      className="mt-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest bg-[#ffaa00]/10 border border-[#ffaa00]/40 text-[#ffd566] hover:bg-[#ffaa00]/20"
                    >
                      Retry payout
                    </button>
                  )}
                </Td>
                <Td><StatusPill status={o.status} /></Td>
                <Td><span className="text-[11px] text-[#606070] font-mono">{o.created_at?.slice(0, 10)}</span></Td>
                <Td>
                  <Link to={`/orders/${o.id}`} target="_blank" className="text-[#00ff88] hover:underline inline-flex items-center gap-1 text-xs">
                    Open <ExternalLink size={11} />
                  </Link>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-4 py-3 font-medium">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3 align-top">{children}</td>;

// ─── Creators Tab ──────────────────────────────────────────────────────
function CreatorsAdmin() {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    adminListCreators().then(setCreators).catch(() => setCreators([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (c) => {
    if (!window.confirm(`Permanently delete creator ${c.username} and their account? This cannot be undone.`)) return;
    try {
      await adminDeleteCreator(c.id);
      toast.success(`Deleted ${c.username}`);
      setCreators((arr) => arr.filter((x) => x.id !== c.id));
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed"));
    }
  };

  const onCensor = async (c) => {
    try {
      await adminPatchCreator(c.id, { censored: true, moderation_note: "Hidden by admin" });
      toast.success(`${c.username} hidden from search`);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Update failed"));
    }
  };

  const onUncensor = async (c) => {
    try {
      await adminPatchCreator(c.id, { censored: false, status: "online", moderation_note: null });
      toast.success(`${c.username} restored`);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Update failed"));
    }
  };

  const onToggleVerify = async (c) => {
    try {
      const res = await adminVerifyCreator(c.id);
      setCreators((arr) => arr.map((x) => (x.id === c.id ? { ...x, verified: res.verified } : x)));
      toast.success(res.verified ? `${c.username} verified` : `${c.username} unverified`);
    } catch (e) {
      toast.error(formatApiError(e, "Verify failed"));
    }
  };

  return (
    <div data-testid="admin-creators">
      {loading && <div className="text-[#606070] text-sm py-6">Loading creators…</div>}
      {!loading && creators.length === 0 && <div className="text-[#606070] text-sm py-6 italic">No creators yet.</div>}

      <div className="grid md:grid-cols-2 gap-3">
        {creators.map((c) => (
          <div key={c.id} data-testid={`admin-creator-${c.id}`} className="cc-surface rounded-xl p-4 border border-[#15152a]">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-lg overflow-hidden border border-[#15152a] bg-[#06060c] flex items-center justify-center text-2xl text-[#00ff88] shrink-0">
                {c.avatar_url ? <img src={c.avatar_url} alt="" className="w-full h-full object-cover" /> : c.glyph}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/creators/${c.id}`} target="_blank" className="text-white font-medium hover:underline">{c.username}</Link>
                  {c.verified ? (
                    <span
                      data-testid={`admin-verified-yes-${c.id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#00ff88] bg-[#00ff88]/10 border border-[#00ff88]/30 px-1.5 py-0.5 rounded"
                    >
                      <BadgeCheck size={11} /> Verified
                    </span>
                  ) : (
                    <span
                      data-testid={`admin-verified-no-${c.id}`}
                      className="text-[10px] uppercase tracking-widest text-[#606070]"
                      title="Not verified"
                    >
                      —
                    </span>
                  )}
                  <span className="text-[10px] uppercase tracking-widest text-[#606070]">{c.status}</span>
                  {c.censored && <span className="text-[10px] uppercase tracking-widest text-[#ff8888] bg-[#ff4444]/10 px-1.5 py-0.5 rounded">Censored</span>}
                </div>
                <div className="text-[11px] text-[#606070] mt-0.5">{(c.games || []).slice(0, 4).join(" · ")}</div>
                <div className="text-[11px] text-[#606070] mt-0.5 font-mono">${c.price} · {c.avg_delivery_days ?? 7}d max</div>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <button
                  onClick={() => onToggleVerify(c)}
                  data-testid={`admin-verify-${c.id}`}
                  className={`p-1.5 border rounded-md transition-colors ${
                    c.verified
                      ? "text-[#06060c] bg-[#00ff88] hover:bg-[#00e67a] border-[#00ff88] shadow-[0_0_12px_-2px_rgba(0,255,136,0.6)]"
                      : "text-[#a0a0b0] hover:text-[#00ff88] hover:bg-[#00ff88]/10 border-[#15152a]"
                  }`}
                  title={c.verified ? "Unverify creator" : "Verify creator"}
                  aria-pressed={!!c.verified}
                >
                  <BadgeCheck size={12} />
                </button>
                <button onClick={() => setEditing(c)} data-testid={`admin-edit-${c.id}`} className="p-1.5 text-[#a0a0b0] hover:text-white border border-[#15152a] rounded-md" title="Edit">
                  <Pencil size={12} />
                </button>
                {c.censored ? (
                  <button onClick={() => onUncensor(c)} data-testid={`admin-uncensor-${c.id}`} className="p-1.5 text-[#00ff88] hover:bg-[#00ff88]/10 border border-[#15152a] rounded-md" title="Restore">
                    <ShieldCheck size={12} />
                  </button>
                ) : (
                  <button onClick={() => onCensor(c)} data-testid={`admin-censor-${c.id}`} className="p-1.5 text-[#ffaa00] hover:bg-[#ffaa00]/10 border border-[#15152a] rounded-md" title="Censor / hide">
                    <EyeOff size={12} />
                  </button>
                )}
                <button onClick={() => onDelete(c)} data-testid={`admin-delete-${c.id}`} className="p-1.5 text-[#ff8888] hover:bg-[#ff4444]/10 border border-[#15152a] rounded-md" title="Delete">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
            {c.bio && <div className="text-[11px] text-[#a0a0b0] mt-2 leading-relaxed line-clamp-2">{c.bio}</div>}
          </div>
        ))}
      </div>

      {editing && (
        <CreatorEditDialog
          creator={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setCreators((arr) => arr.map((x) => (x.id === updated.id ? updated : x)));
            setEditing(null);
            toast.success("Creator updated");
          }}
        />
      )}
    </div>
  );
}

function CreatorEditDialog({ creator, onClose, onSaved }) {
  const [form, setForm] = useState({
    bio: creator.bio || "",
    price: creator.price ?? 0,
    avg_delivery_days: creator.avg_delivery_days ?? 7,
    games: (creator.games || []).join(", "),
    services: (creator.services || []).join(", "),
    status: creator.status || "online",
    moderation_note: creator.moderation_note || "",
    avatar_url: creator.avatar_url || "",
    portfolio: (creator.portfolio || []).map((p) => p.url).filter(Boolean),
  });
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [portfolioUploadingIdx, setPortfolioUploadingIdx] = useState(null);
  const fileRef = React.useRef(null);
  const pfInputRef = React.useRef(null);
  const pfTargetIdxRef = React.useRef(null);

  const onPickAvatar = () => fileRef.current?.click();

  const onAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5 MB)");
      return;
    }
    setAvatarUploading(true);
    try {
      const res = await uploadAvatar(file);
      setForm((f) => ({ ...f, avatar_url: res.url }));
      toast.success("Avatar uploaded — click Save to apply");
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      setAvatarUploading(false);
    }
  };

  const onClearAvatar = () => setForm((f) => ({ ...f, avatar_url: "" }));

  // ─── Portfolio editors ─────────────────────────────────────────
  const addPortfolioRow = () =>
    setForm((f) => ({ ...f, portfolio: [...f.portfolio, ""] }));
  const removePortfolioRow = (idx) =>
    setForm((f) => ({ ...f, portfolio: f.portfolio.filter((_, i) => i !== idx) }));
  const updatePortfolioRow = (idx, value) =>
    setForm((f) => ({
      ...f,
      portfolio: f.portfolio.map((u, i) => (i === idx ? value : u)),
    }));
  const triggerPortfolioUpload = (idx) => {
    pfTargetIdxRef.current = idx;
    pfInputRef.current?.click();
  };
  const onPortfolioFile = async (e) => {
    const idx = pfTargetIdxRef.current;
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file || idx == null) return;
    setPortfolioUploadingIdx(idx);
    try {
      const res = await uploadAdminPortfolio(file);
      updatePortfolioRow(idx, res.url);
      toast.success("Media uploaded — click Save to apply");
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      setPortfolioUploadingIdx(null);
      pfTargetIdxRef.current = null;
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await adminPatchCreator(creator.id, {
        bio: form.bio,
        price: Number(form.price) || 0,
        avg_delivery_days: Number(form.avg_delivery_days) || 1,
        games: form.games.split(",").map((s) => s.trim()).filter(Boolean),
        services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
        status: form.status,
        moderation_note: form.moderation_note || null,
        avatar_url: form.avatar_url || "",
        portfolio_urls: form.portfolio.map((u) => (u || "").trim()).filter(Boolean),
      });
      onSaved(updated);
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-edit-dialog" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="cc-elevated rounded-2xl p-6 w-full max-w-lg my-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="cc-overline">Edit creator (admin)</div>
            <h3 className="text-xl font-semibold text-white mt-1">{creator.glyph} {creator.username}</h3>
          </div>
          <button onClick={onClose} className="text-[#606070] hover:text-white"><X size={16} /></button>
        </div>

        <div className="space-y-3">
          <Field label="Profile picture">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-xl overflow-hidden border border-[#15152a] bg-[#06060c] flex items-center justify-center text-2xl text-[#00ff88] shrink-0">
                {form.avatar_url ? (
                  <img src={form.avatar_url} alt="avatar preview" className="w-full h-full object-cover" />
                ) : (
                  creator.glyph || "◈"
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={onAvatarFile}
                className="hidden"
                data-testid="admin-edit-avatar-input"
              />
              <button
                type="button"
                onClick={onPickAvatar}
                disabled={avatarUploading}
                data-testid="admin-edit-avatar-pick"
                className={`text-xs border border-[#15152a] bg-[#0a0a14] text-white rounded-md px-3 py-2 hover:border-[#00ff88]/40 ${avatarUploading ? "opacity-60 cursor-wait" : ""}`}
              >
                {avatarUploading ? "Uploading…" : (form.avatar_url ? "Replace image" : "Upload image")}
              </button>
              {form.avatar_url && (
                <button
                  type="button"
                  onClick={onClearAvatar}
                  data-testid="admin-edit-avatar-clear"
                  className="text-xs text-[#ff8888] hover:underline"
                >
                  Remove
                </button>
              )}
            </div>
          </Field>
          <Field label="Bio (will be visible publicly — censor sensitive parts here)">
            <textarea
              data-testid="admin-edit-bio" rows={4} value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white cc-focus-ring"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Price ($)">
              <input data-testid="admin-edit-price" type="number" value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white font-mono cc-focus-ring" />
            </Field>
            <Field label="Max delivery (days)">
              <input data-testid="admin-edit-delivery" type="number" value={form.avg_delivery_days}
                onChange={(e) => setForm({ ...form, avg_delivery_days: e.target.value })}
                className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white font-mono cc-focus-ring" />
            </Field>
          </div>
          <Field label="Games (comma-separated)">
            <input data-testid="admin-edit-games" value={form.games}
              onChange={(e) => setForm({ ...form, games: e.target.value })}
              className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white font-mono cc-focus-ring" />
          </Field>
          <Field label="Services (comma-separated)">
            <input data-testid="admin-edit-services" value={form.services}
              onChange={(e) => setForm({ ...form, services: e.target.value })}
              className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white font-mono cc-focus-ring" />
          </Field>
          <Field label="Status">
            <select
              data-testid="admin-edit-status" value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white cc-focus-ring"
            >
              <option value="online">online</option>
              <option value="busy">busy</option>
              <option value="offline">offline</option>
              <option value="censored">censored</option>
            </select>
          </Field>
          <Field label="Moderation note (internal)">
            <input data-testid="admin-edit-note" value={form.moderation_note}
              onChange={(e) => setForm({ ...form, moderation_note: e.target.value })}
              placeholder="Reason for moderation, visible only to admins"
              className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white cc-focus-ring" />
          </Field>

          <Field label="Portfolio media (URLs or uploaded files)">
            <input
              ref={pfInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
              onChange={onPortfolioFile}
              className="hidden"
              data-testid="admin-edit-portfolio-input"
            />
            <div className="space-y-2">
              {form.portfolio.length === 0 && (
                <div className="text-[11px] text-[#606070] italic">No portfolio media yet.</div>
              )}
              {form.portfolio.map((url, idx) => (
                <div
                  key={idx}
                  data-testid={`admin-edit-portfolio-row-${idx}`}
                  className="flex items-center gap-2"
                >
                  <Link2 size={13} className="text-[#606070] shrink-0" />
                  <input
                    value={url}
                    onChange={(e) => updatePortfolioRow(idx, e.target.value)}
                    placeholder="https://youtu.be/… or /api/uploads/files/…"
                    data-testid={`admin-edit-portfolio-url-${idx}`}
                    className="flex-1 min-w-0 bg-[#06060c] border border-[#15152a] rounded-md px-2.5 py-1.5 text-[12px] text-white font-mono cc-focus-ring"
                  />
                  <button
                    type="button"
                    onClick={() => triggerPortfolioUpload(idx)}
                    disabled={portfolioUploadingIdx === idx}
                    data-testid={`admin-edit-portfolio-upload-${idx}`}
                    title="Upload a file to replace this URL"
                    className={`shrink-0 w-8 h-8 rounded-md border border-[#15152a] text-[#a0a0b0] hover:text-[#00ff88] hover:border-[#00ff88]/40 flex items-center justify-center ${portfolioUploadingIdx === idx ? "opacity-60 cursor-wait" : ""}`}
                  >
                    {portfolioUploadingIdx === idx ? (
                      <span className="w-3 h-3 border-2 border-[#00ff88] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Upload size={12} />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => removePortfolioRow(idx)}
                    data-testid={`admin-edit-portfolio-delete-${idx}`}
                    title="Remove this entry"
                    className="shrink-0 w-8 h-8 rounded-md border border-[#15152a] text-[#ff8888] hover:bg-[#ff4444]/10 flex items-center justify-center"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addPortfolioRow}
                data-testid="admin-edit-portfolio-add"
                className="text-[11px] border border-dashed border-[#15152a] text-[#a0a0b0] hover:text-[#00ff88] hover:border-[#00ff88]/40 rounded-md px-3 py-1.5 inline-flex items-center gap-1"
              >
                + Add portfolio media
              </button>
            </div>
          </Field>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#a0a0b0] hover:text-white">Cancel</button>
          <button data-testid="admin-edit-save" onClick={save} disabled={saving}
            className="cc-btn-primary rounded-lg px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50">
            <Save size={13} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="cc-overline mb-1.5">{label}</div>
    {children}
  </div>
);



// ─── Payouts Tab (LTC creator withdrawals via OxaPay) ───────────────────────
function PayoutsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState({});

  const load = () => {
    setLoading(true);
    adminListPayouts(filter ? { status: filter } : {})
      .then(setItems)
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const onResync = async (wd) => {
    setBusy((b) => ({ ...b, [wd.id]: true }));
    try {
      const res = await adminResyncPayout(wd.id);
      toast.success(`Status · ${res.status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Resync failed"));
    } finally {
      setBusy((b) => ({ ...b, [wd.id]: false }));
    }
  };

  return (
    <div data-testid="admin-payouts" className="cc-surface rounded-xl p-6">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="cc-overline">Creator withdrawals · OxaPay</div>
          <h3 className="text-xl font-semibold text-white">LTC payouts</h3>
          <p className="text-xs text-[#a0a0b0] mt-1 max-w-xl">
            Every payout is created via OxaPay's <code className="text-[#00ff88]">/v1/payout</code> endpoint and broadcast on-chain
            within seconds. The IPN webhook flips <code className="text-[#a0a0b0]">processing</code> → <code className="text-[#00ff88]">confirmed</code>.
            If the webhook is missed, click <strong className="text-white">Resync</strong>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="payouts-status-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-[#06060c] border border-[#15152a] rounded-md px-3 py-1.5 text-xs text-white"
          >
            <option value="">All statuses</option>
            <option value="processing">processing</option>
            <option value="confirming">confirming</option>
            <option value="confirmed">confirmed</option>
            <option value="failed">failed</option>
          </select>
          <button
            data-testid="payouts-refresh"
            onClick={load}
            className="text-[11px] text-[#a0a0b0] hover:text-white inline-flex items-center gap-1 border border-[#15152a] bg-[#0a0a14] rounded-md px-3 py-1.5"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-[#606070]">Loading payouts…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-[#606070]">No withdrawals match this filter.</div>
      ) : (
        <div className="space-y-3">
          {items.map((wd) => {
            const statusUp = (wd.status || "").toLowerCase();
            return (
              <div key={wd.id} data-testid={`payout-row-${wd.id}`} className="border border-[#15152a] rounded-lg p-4 bg-[#06060c]">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest border ${
                        statusUp === "confirmed" ? "bg-[#00ff88]/10 text-[#00ff88] border-[#00ff88]/30"
                        : statusUp === "failed" ? "bg-[#ff4444]/10 text-[#ff8888] border-[#ff4444]/30"
                        : "bg-[#0d0d1a] text-[#a0a0b0] border-[#15152a]"
                      }`}>
                        {wd.status || "—"}
                      </span>
                      <span className="text-sm text-white font-mono">${wd.amount}</span>
                      <span className="text-xs text-[#606070]">·</span>
                      <span className="text-xs text-[#a0a0b0] font-mono">{wd.amount_ltc} LTC</span>
                      <span className="text-xs text-[#606070]">·</span>
                      <span className="text-[11px] text-[#606070] font-mono truncate max-w-[300px]">{wd.destination}</span>
                    </div>
                    <div className="text-[11px] text-[#606070] font-mono mt-1">
                      wd · {wd.id.slice(0, 8)} · oxapay · {wd.payout_id || "—"} · {wd.created_at?.slice(0, 19).replace("T", " ")}
                    </div>
                    {wd.tx_hash && (
                      <a
                        href={`https://blockchair.com/litecoin/transaction/${wd.tx_hash}`}
                        target="_blank" rel="noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#00ff88] hover:underline font-mono break-all"
                      >
                        {wd.tx_hash.slice(0, 28)}… <ExternalLink size={10} />
                      </a>
                    )}
                    {wd.error && <div className="text-[11px] text-[#ff8888] mt-1">{wd.error}</div>}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`payout-resync-${wd.id}`}
                      onClick={() => onResync(wd)}
                      disabled={busy[wd.id]}
                      className="text-[11px] text-[#a0a0b0] hover:text-white border border-[#15152a] bg-[#0a0a14] rounded-md px-2.5 py-1.5 inline-flex items-center gap-1 disabled:opacity-50"
                    >
                      <RefreshCw size={12} /> Resync
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
